import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { formatApiError, useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Check, Loader2, ArrowRight, Sparkles, Briefcase } from "lucide-react";
import { toast } from "sonner";

export default function Pricing() {
  const [plans, setPlans] = useState(null);
  const [loading, setLoading] = useState(null);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/payments/plans")
      .then(({ data }) => setPlans(data.plans))
      .catch((err) => toast.error(formatApiError(err)));
  }, []);

  const startCheckout = async (lookup_key) => {
    if (!user) {
      navigate("/register");
      return;
    }
    setLoading(lookup_key);
    try {
      const { data } = await api.post("/payments/checkout", { lookup_key, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiError(err));
      setLoading(null);
    }
  };

  return (
    <div className="mx-auto max-w-6xl px-6 lg:px-10 py-16 lg:py-20" data-testid="pricing-page">
      <div className="text-center max-w-2xl mx-auto">
        <div className="eyebrow">Tarifs</div>
        <h1 className="mt-3 font-display text-4xl lg:text-5xl font-black tracking-tighter">
          Un modèle simple, transparent.
        </h1>
        <p className="mt-4 text-muted-foreground">
          Le diagnostic est gratuit et illimité. Payez uniquement pour accéder aux rapports détaillés.
        </p>
      </div>

      <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* Free */}
        <Card className="p-8 border-black/10 flex flex-col" data-testid="plan-free">
          <div className="eyebrow">Gratuit</div>
          <div className="mt-2 font-display text-4xl font-black">0 €</div>
          <p className="mt-1 text-xs text-muted-foreground">pour toujours</p>
          <ul className="mt-8 space-y-3 text-sm flex-1">
            {["Diagnostic technique complet", "Score global et par catégorie", "Aperçu des problèmes détectés", "Historique de vos diagnostics"].map((f) => (
              <li key={f} className="flex items-start gap-2"><Check className="h-4 w-4 text-emerald-600 mt-0.5 shrink-0" /><span>{f}</span></li>
            ))}
          </ul>
          <Button variant="outline" className="mt-8 rounded-full h-11" onClick={() => navigate(user ? "/diagnostic" : "/register")} data-testid="cta-free">
            {user ? "Lancer un diagnostic" : "Créer un compte"}
          </Button>
        </Card>

        {/* One time */}
        {plans && plans.filter((p) => p.type === "one_time").map((p) => (
          <Card key={p.lookup_key} className="p-8 border-black/10 flex flex-col" data-testid={`plan-${p.lookup_key}`}>
            <div className="eyebrow">Rapport à l'unité</div>
            <div className="mt-2 font-display text-4xl font-black">{p.price_display}</div>
            <p className="mt-1 text-xs text-muted-foreground">paiement unique</p>
            <ul className="mt-8 space-y-3 text-sm flex-1">
              {p.features.map((f) => (
                <li key={f} className="flex items-start gap-2"><Check className="h-4 w-4 text-emerald-600 mt-0.5 shrink-0" /><span>{f}</span></li>
              ))}
            </ul>
            <Button className="mt-8 rounded-full h-11" onClick={() => startCheckout(p.lookup_key)} disabled={loading === p.lookup_key} data-testid={`cta-${p.lookup_key}`}>
              {loading === p.lookup_key ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Choisir <ArrowRight className="h-4 w-4 ml-2" /></>}
            </Button>
          </Card>
        ))}

        {/* Premium (particulier) */}
        {plans && plans.filter((p) => p.type === "subscription" && p.lookup_key === "premium_monthly").map((p) => (
          <Card key={p.lookup_key} className="p-8 border-primary/40 flex flex-col relative bg-primary/5" data-testid={`plan-${p.lookup_key}`}>
            <Badge className="absolute -top-3 left-8 bg-primary text-white rounded-full border-0 flex items-center gap-1">
              <Sparkles className="h-3 w-3" /> Recommandé
            </Badge>
            <div className="eyebrow">Premium — Particulier</div>
            <div className="mt-2 font-display text-4xl font-black">{p.price_display}</div>
            <p className="mt-1 text-xs text-muted-foreground">sans engagement</p>
            <ul className="mt-8 space-y-3 text-sm flex-1">
              {p.features.map((f) => (
                <li key={f} className="flex items-start gap-2"><Check className="h-4 w-4 text-emerald-600 mt-0.5 shrink-0" /><span>{f}</span></li>
              ))}
            </ul>
            <Button className="mt-8 rounded-full h-11" onClick={() => startCheckout(p.lookup_key)} disabled={loading === p.lookup_key} data-testid={`cta-${p.lookup_key}`}>
              {loading === p.lookup_key ? <Loader2 className="h-4 w-4 animate-spin" /> : <>S'abonner <ArrowRight className="h-4 w-4 ml-2" /></>}
            </Button>
          </Card>
        ))}

        {/* Pro (techniciens) */}
        {plans && plans.filter((p) => p.type === "subscription" && p.lookup_key === "pro_monthly").map((p) => (
          <Card key={p.lookup_key} className="p-8 border-slate-900 flex flex-col relative bg-slate-950 text-white" data-testid={`plan-${p.lookup_key}`}>
            <Badge className="absolute -top-3 left-8 bg-amber-400 text-slate-950 rounded-full border-0 flex items-center gap-1">
              <Briefcase className="h-3 w-3" /> Pro
            </Badge>
            <div className="eyebrow text-white/60">Techniciens & Installateurs</div>
            <div className="mt-2 font-display text-4xl font-black">{p.price_display}</div>
            <p className="mt-1 text-xs text-white/50">sans engagement · TVA récupérable</p>
            <ul className="mt-8 space-y-3 text-sm flex-1">
              {p.features.map((f) => (
                <li key={f} className="flex items-start gap-2"><Check className="h-4 w-4 text-amber-400 mt-0.5 shrink-0" /><span>{f}</span></li>
              ))}
            </ul>
            <div className="mt-4 text-[11px] uppercase tracking-widest text-amber-400/80 font-semibold">
              Certaines fonctionnalités arrivent bientôt
            </div>
            <Button className="mt-4 rounded-full h-11 bg-amber-400 hover:bg-amber-300 text-slate-950" onClick={() => startCheckout(p.lookup_key)} disabled={loading === p.lookup_key} data-testid={`cta-${p.lookup_key}`}>
              {loading === p.lookup_key ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Rejoindre Pro <ArrowRight className="h-4 w-4 ml-2" /></>}
            </Button>
          </Card>
        ))}
      </div>

      <div className="mt-12 text-center text-xs text-muted-foreground">
        Paiement sécurisé par Stripe · Résiliation à tout moment pour l'abonnement · TVA incluse
      </div>

      <div className="mt-6 mx-auto max-w-xl rounded-xl border border-dashed border-primary/30 bg-primary/5 p-5 text-center" data-testid="promo-hint">
        <div className="text-xs uppercase tracking-widest font-bold text-primary">Astuce</div>
        <p className="mt-2 text-sm">
          Ajoutez un <span className="font-mono font-bold">code promo</span> lors du paiement Stripe.
          Essayez <span className="font-mono font-bold text-primary">SPA10</span> pour 10% de réduction.
        </p>
      </div>
    </div>
  );
}
